import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Globe, ExternalLink, ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import SnapshotView from '@/components/dashboard/SnapshotView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalizeUrl = (u) => (u ? (u.startsWith('http') ? u : `https://${u}`) : '');
const prettyHost = (u) => { try { return new URL(normalizeUrl(u)).hostname.replace(/^www\./, ''); } catch { return u; } };

export default function PublicSnapshot() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(undefined); // undefined=loading, null=error
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/public/growth-snapshot/${token}`)
      .then((res) => setData(res.data))
      .catch((e) => { setData(null); setError(e.response?.status === 404 ? 'This snapshot link is no longer available.' : 'Could not load this snapshot.'); });
  }, [token]);

  if (data === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-400" data-testid="public-snapshot-loading"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading snapshot…</div>;
  }
  if (data === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="public-snapshot-error">
        <div className="text-center">
          <ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Outfit' }}>Snapshot unavailable</h1>
          <p className="mt-2 text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  const website = normalizeUrl(data.website);
  const generatedAt = data.generated_at ? new Date(data.generated_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '';

  const CTAs = ({ variant }) => (
    <div className={`flex flex-col sm:flex-row gap-3 ${variant === 'hero' ? '' : 'justify-center'}`} data-testid={`public-cta-${variant}`}>
      <button
        onClick={() => navigate('/auth?mode=signup')}
        className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-white text-violet-700 font-bold text-sm shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all"
        data-testid="public-signup-cta"
      >
        Sign up <ArrowRight className="w-4 h-4" />
      </button>
      <a
        href="https://lawagency360.com" target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-white/10 text-white font-bold text-sm border border-white/30 hover:bg-white/20 transition-all"
        data-testid="public-viewwebsite-cta"
      >
        View website <ExternalLink className="w-4 h-4" />
      </a>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-snapshot-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-violet-700 via-indigo-700 to-violet-800 text-white">
        <div className="max-w-6xl mx-auto px-6 py-14 sm:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-[11px] font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" /> Growth Opportunity Snapshot
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight" style={{ fontFamily: 'Outfit' }}>
            {data.firm_name || 'Your firm'}’s<br />Search &amp; AI Growth Snapshot
          </h1>
          <p className="mt-4 text-base text-violet-100 max-w-2xl leading-relaxed">
            A view-only AI-estimated analysis of the search and AI-engine growth opportunities for {data.firm_name || 'your firm'}.
            {generatedAt ? ` Prepared ${generatedAt}.` : ''}
          </p>

          {/* Prospect website card */}
          {website && (
            <a href={website} target="_blank" rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/10 border border-white/20 hover:bg-white/15 transition-all group max-w-full"
              data-testid="public-website-card">
              <img src={`https://www.google.com/s2/favicons?domain=${prettyHost(website)}&sz=64`} alt="" className="w-8 h-8 rounded-lg bg-white/90 p-1 shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
              <span className="min-w-0">
                <span className="block text-[11px] uppercase tracking-wider text-violet-200">Prospect website</span>
                <span className="flex items-center gap-1.5 font-semibold truncate"><Globe className="w-4 h-4 shrink-0" /> {prettyHost(website)}</span>
              </span>
              <ExternalLink className="w-4 h-4 text-violet-200 group-hover:text-white shrink-0" />
            </a>
          )}

          <div className="mt-8"><CTAs variant="hero" /></div>
        </div>
      </div>

      {/* Snapshot body */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <SnapshotView snapshot={data} />

        {/* Bottom conversion CTA */}
        <div className="mt-12 rounded-3xl bg-gradient-to-br from-violet-600 via-indigo-600 to-violet-700 p-8 sm:p-12 shadow-2xl shadow-violet-500/30 text-center" data-testid="public-bottom-cta">
          <Sparkles className="w-7 h-7 text-white mx-auto" />
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Ready to turn these opportunities into clients?</h2>
          <p className="mt-3 text-sm text-violet-100 max-w-2xl mx-auto leading-relaxed">
            Sign up to unlock live AI visibility tracking, position monitoring, and hands-on implementation for {data.firm_name || 'your firm'}.
          </p>
          <div className="mt-7"><CTAs variant="bottom" /></div>
        </div>

        <p className="mt-8 text-center text-xs text-slate-400">Powered by Law Agency 360 · This is a view-only snapshot shared with you.</p>
      </div>
    </div>
  );
}
